import Dialog from "../../General/ConfirmableDialog";
import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import ConfirmActionButton from "../ConfirmActionButton/ConfirmActionButton";

interface IncidentProofPreviewModalProps {
  open: boolean;
  onClose: () => void;
  incident: any;
}

interface IncidentProof {
  id: string;
  incidentId: string;
  fileName: string;
}

interface ProofAsset {
  url: string;
  contentType: string;
}

const loadAuthorizedFile = async (
  fileName: string,
  token: string | null | undefined
): Promise<ProofAsset> => {
  const response = await fetch(
    `${apiVariable}/incident/preview?fileName=${encodeURIComponent(fileName)}`,
    {
      headers: {
        Authorization: `Bearer ${token ?? ""}`,
      },
    }
  );

  if (!response.ok) {
    throw new Error(`File not found for fileName=${fileName}`);
  }

  const blob = await response.blob();
  const contentType = response.headers.get("Content-Type") ?? "";

  return {
    url: URL.createObjectURL(blob),
    contentType,
  };
};

const IncidentProofPreviewModal: React.FC<IncidentProofPreviewModalProps> = ({
  open,
  onClose,
  incident,
}) => {
  const { token } = useAuth();
  const [assets, setAssets] = useState<Record<string, ProofAsset>>({});
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [assetError, setAssetError] = useState<string | null>(null);
  const [activePreview, setActivePreview] = useState<{
    proof: IncidentProof;
    asset: ProofAsset;
  } | null>(null);

  const {
    data: proofs = [],
    isLoading,
    isError,
    refetch,
  } = useQuery<IncidentProof[]>({
    queryKey: ["incidentProofs", incident?.id],
    queryFn: async () => {
      const response = await axios.get(
        `${apiVariable}/incident/${incident.id}/proofs`,
        {
          headers: {
            Authorization: `Bearer ${token ?? ""}`,
          },
        }
      );
      return response.data;
    },
    enabled: open && !!incident?.id,
  });

  useEffect(() => {
    if (open && incident?.id) {
      refetch();
    }
  }, [open, incident?.id, refetch]);

  useEffect(() => {
    let isMounted = true;

    const loadAssets = async () => {
      if (!open) {
        return;
      }

      if (!proofs.length) {
        setAssets({});
        setAssetError(null);
        setLoadingAssets(false);
        return;
      }

      setAssets({});
      setLoadingAssets(true);
      setAssetError(null);

      let encounteredError = false;

      const results = await Promise.all(
        proofs.map(async (proof) => {
          try {
            const asset = await loadAuthorizedFile(proof.fileName, token);
            return { proofId: proof.id, asset };
          } catch (error) {
            console.error("Error loading incident proof:", error);
            encounteredError = true;
            return null;
          }
        })
      );

      if (!isMounted) {
        results.forEach((result) => {
          if (result) {
            URL.revokeObjectURL(result.asset.url);
          }
        });
        return;
      }

      const newAssets: Record<string, ProofAsset> = {};
      results.forEach((result) => {
        if (result) {
          newAssets[result.proofId] = result.asset;
        }
      });

      setAssets(newAssets);
      setLoadingAssets(false);
      if (encounteredError) {
        setAssetError("Some proofs could not be loaded.");
      }
    };

    loadAssets();

    return () => {
      isMounted = false;
    };
  }, [open, proofs, token]);

  useEffect(() => {
    if (!open) {
      setAssets({});
      setAssetError(null);
      setActivePreview(null);
    }
  }, [open]);

  useEffect(() => {
    return () => {
      Object.values(assets).forEach(({ url }) => URL.revokeObjectURL(url));
    };
  }, [assets]);

  const handleClosePreview = () => {
    setActivePreview(null);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Proofs: Incident #{incident?.id}</DialogTitle>
        <DialogContent>
          {isLoading || loadingAssets ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              p={4}
            >
              <CircularProgress />
            </Box>
          ) : isError ? (
            <Typography color="error" sx={{ py: 2 }}>
              Unable to load incident proofs.
            </Typography>
          ) : !proofs.length ? (
            <Typography sx={{ py: 2 }}>
              No proofs found for this incident.
            </Typography>
          ) : (
            <Box display="flex" flexDirection="column" gap={3} sx={{ py: 1 }}>
              {assetError && (
                <Typography color="error">{assetError}</Typography>
              )}
              {proofs.map((proof) => {
                const asset = assets[proof.id];
                return (
                  <Box
                    key={proof.id}
                    display="flex"
                    flexDirection="column"
                    gap={1}
                  >
                    <Typography variant="subtitle1">
                      {proof.fileName}
                    </Typography>
                    {!asset ? (
                      <Box display="flex" justifyContent="center" p={2}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          position: "relative",
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "divider",
                          overflow: "hidden",
                          backgroundColor: "background.paper",
                        }}
                      >
                        {asset.contentType.includes("pdf") ? (
                          <Box
                            component="iframe"
                            src={asset.url}
                            sx={{ border: 0, width: "100%", minHeight: 320 }}
                            title={proof.fileName}
                          />
                        ) : (
                          <Box
                            component="img"
                            src={asset.url}
                            alt={proof.fileName}
                            sx={{
                              display: "block",
                              width: "100%",
                              height: 320,
                              objectFit: "contain",
                              backgroundColor: "background.default",
                            }}
                          />
                        )}
                        <Box
                          sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            display: "flex",
                          }}
                        >
                          <Tooltip title="Open larger view">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => setActivePreview({ proof, asset })}
                            >
                              <ZoomInIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    )}
                    {asset && (
                      <Box>
                        <Button
                          variant="outlined"
                          size="small"
                          component="a"
                          href={asset.url}
                          download={proof.fileName}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            mb: 1
                          }}
                        >
                          Download
                        </Button>
                        <ConfirmActionButton
                          id={proof.id}
                          entityType="IncidentProof"
                          actionType="delete"
                          entityLabel="Incident proof"
                          queryKey="incidentProofs"
                          buttonLabel="Delete"
                          buttonColor="error"
                        />
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={!!activePreview}
        onClose={handleClosePreview}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>{activePreview?.proof.fileName}</DialogTitle>
        <DialogContent dividers>
          {activePreview &&
            (activePreview.asset.contentType.includes("pdf") ? (
              <Box
                component="iframe"
                src={activePreview.asset.url}
                sx={{ border: 0, width: "100%", minHeight: "75vh" }}
                title={activePreview.proof.fileName}
              />
            ) : (
              <Box
                component="img"
                src={activePreview.asset.url}
                alt={activePreview.proof.fileName}
                sx={{
                  display: "block",
                  width: "100%",
                  maxHeight: "80vh",
                  objectFit: "contain",
                  backgroundColor: "background.default",
                }}
              />
            ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePreview} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default IncidentProofPreviewModal;
